/**
 * Roles (design 2C): clinics create their own; the permission catalogue is
 * DATA from Auth's registry — the rows are slots, nothing invented. Full
 * clinic administration is protected platform authority, not a role — it is
 * not in this list and has no lifecycle here. A refused mutation keeps the
 * editor and everything typed, and names the refusal (ZOY-378); an editable
 * role can be deactivated (confirm-first, assignments kept) and reactivated
 * (ZOY-379) — deactivate only, never delete.
 */

import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, TextField } from '@/ui/kit';
import { ConfirmDialog, FieldGrid, FormCard, SelectField, Span2 } from '@/ui/formkit';
import { ApiError, NetworkError } from '@/core/api/errors';
import {
  createRole,
  listRoles,
  permissionCatalog,
  updateRole,
  type PermissionRow,
  type RoleRow,
} from '@/core/auth/adminApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { permissionDescription, permissionLabel } from './permissionLabels';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'detail'; role: RoleRow };

/**
 * ZOY-378: every role mutation names its refusal in clinic words — never a
 * technical code, never silence. The known cases carry their own copy.
 */
export function roleSaveErrorKey(err: unknown): string {
  if (err instanceof NetworkError) return 'signin.netBody';
  if (err instanceof ApiError) {
    if (err.status === 409) return 'roles.errDuplicate';
    if (err.status === 403) return 'roles.errForbidden';
    if (err.status === 400) return 'roles.errRefused';
  }
  return 'common.failedKept';
}

export function RolesTab() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>({ kind: 'list' });

  const rolesQuery = useQuery({
    queryKey: ['roles', clinicId],
    queryFn: () => listRoles(clinicId),
    staleTime: 60_000,
  });
  const catalogQuery = useQuery({
    queryKey: ['perm-catalog'],
    queryFn: permissionCatalog,
    staleTime: 10 * 60_000,
  });

  // A renamed or deactivated role also reads on every member's row in the staff list.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['roles', clinicId] });
    void queryClient.invalidateQueries({ queryKey: ['members', clinicId] });
  };

  if (view.kind === 'create') {
    return (
      <RoleEditor
        clinicId={clinicId}
        roles={rolesQuery.data ?? []}
        catalog={catalogQuery.data ?? []}
        onDone={() => {
          invalidate();
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  if (view.kind === 'detail') {
    return (
      <RoleEditor
        clinicId={clinicId}
        role={view.role}
        roles={rolesQuery.data ?? []}
        catalog={catalogQuery.data ?? []}
        onDone={() => {
          invalidate();
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxInlineSize: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className={a.pageSub} style={{ margin: 0 }}>
          {t('roles.sub')}
        </p>
        <Button variant="primary" onClick={() => setView({ kind: 'create' })}>
          {t('roles.create')}
        </Button>
      </div>

      <div className={a.cardsRow} style={{ inlineSize: '100%' }}>
        {/* Protected authority — platform grant, not a role. */}
        <div
          className={a.chooserCard}
          style={{ background: 'var(--zc-sage-soft)', borderColor: 'var(--zc-sage-soft-border)' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span className={a.chooserName}>{t('roles.protectedTitle')}</span>
            <Chip kind="warn">{t('roles.protectedChip')}</Chip>
          </div>
          <span style={{ fontSize: 12.5, color: 'var(--zc-ink-2)', lineHeight: 1.6 }}>
            {t('roles.protectedBody')}
          </span>
        </div>

        {(rolesQuery.data ?? []).map((r) => (
          <div key={r.id} className={a.chooserCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span className={a.chooserName}>{r.name}</span>
              {r.active === false ? (
                <Chip kind="quiet">{t('roles.inactive')}</Chip>
              ) : (
                <Chip kind="ok">{t('roles.people', { n: r.members ?? 0 })}</Chip>
              )}
            </div>
            {r.description ? (
              <span style={{ fontSize: 12.5, color: 'var(--zc-ink-2)', lineHeight: 1.6 }}>
                {r.description}
              </span>
            ) : null}
            <Button variant="quiet" onClick={() => setView({ kind: 'detail', role: r })}>
              {t('common.edit')}
            </Button>
          </div>
        ))}
      </div>

      <Notice kind="info">{t('roles.catalogNote')}</Notice>
    </div>
  );
}

export function RoleEditor(props: {
  clinicId: string;
  role?: RoleRow;
  roles: RoleRow[];
  catalog: PermissionRow[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const editing = props.role !== undefined;

  const [name, setName] = useState(props.role?.name ?? '');
  const [description, setDescription] = useState(props.role?.description ?? '');
  const [copyFrom, setCopyFrom] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(props.role?.permissions ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const inactive = props.role?.active === false;

  const grantableCatalog = useMemo(
    () => props.catalog.filter((p) => p.grantPolicy !== 'platform'),
    [props.catalog],
  );

  const groups = useMemo(() => {
    const byCat = new Map<string, PermissionRow[]>();
    for (const p of grantableCatalog) {
      const cat = p.category ?? '—';
      const arr = byCat.get(cat) ?? [];
      arr.push(p);
      byCat.set(cat, arr);
    }
    return [...byCat.entries()];
  }, [grantableCatalog]);

  const applyCopy = (roleId: string) => {
    setCopyFrom(roleId);
    const src = props.roles.find((r) => r.id === roleId);
    if (src?.permissions) setSelected(new Set(src.permissions));
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? updateRole(props.clinicId, props.role!.id, {
            name: name.trim(),
            description: description.trim() || undefined,
            permissions: [...selected],
          })
        : createRole(props.clinicId, {
            name: name.trim(),
            description: description.trim() || undefined,
            permissions: [...selected],
          }),
    onMutate: () => setError(null),
    onSuccess: props.onDone,
    // ZOY-378: stay mounted — everything typed is still here for a retry.
    onError: (err) => setError(t(roleSaveErrorKey(err)) as string),
  });

  // ZOY-379: deactivate / reactivate — a lifecycle fact, never a delete.
  const setActive = useMutation({
    mutationFn: (active: boolean) => updateRole(props.clinicId, props.role!.id, { active }),
    onMutate: () => setError(null),
    onSuccess: props.onDone,
    onError: (err) => {
      setConfirmingOff(false);
      setError(t(roleSaveErrorKey(err)) as string);
    },
  });

  return (
    <div style={{ maxInlineSize: 760, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FormCard
        title={editing ? props.role!.name : (t('roles.create') as string)}
        sub={editing ? (t('roles.affects') as string) : undefined}
        onSubmit={() => save.mutate()}
        cta={editing ? (t('roles.saveCta') as string) : (t('roles.createCta') as string)}
        ctaDisabled={name.trim() === ''}
        busy={save.isPending}
        secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
      >
        {error && (
          <Notice kind="err" role="alert">
            {error}
          </Notice>
        )}
        {editing && inactive && <Notice kind="warn">{t('roles.inactiveNote')}</Notice>}
        <FieldGrid>
          <TextField
            label={t('roles.name')}
            value={name}
            onChange={setName}
            help={t('roles.nameHelp')}
            requirement={{ word: t('signin.required'), kind: 'required' }}
          />
          {!editing && (
            <SelectField
              label={t('roles.copyFrom')}
              value={copyFrom}
              onChange={applyCopy}
              options={[
                { value: '', label: t('roles.copyNone') as string },
                ...props.roles.map((r) => ({ value: r.id, label: r.name })),
              ]}
              help={t('roles.copyFromHelp')}
            />
          )}
          <Span2>
            <TextField
              label={t('roles.description')}
              value={description}
              onChange={setDescription}
              requirement={{ word: t('signin.optional'), kind: 'optional' }}
            />
          </Span2>
        </FieldGrid>

        <div style={{ marginBlockStart: 10 }}>
          <strong style={{ fontSize: 13, color: 'var(--zc-teal-deep)' }}>
            {t('roles.permissions')}
          </strong>
          {groups.map(([cat, perms]) => (
            <div key={cat} style={{ marginBlockStart: 10 }}>
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: 'var(--zc-muted)',
                  marginBlockEnd: 4,
                }}
              >
                {cat}
              </div>
              {perms.map((p) => {
                const on = selected.has(p.key);
                return (
                  <label
                    key={p.key}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '7px 4px',
                      cursor: 'pointer',
                      borderBlockStart: '1px solid var(--zc-warm)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.currentTarget.checked) next.add(p.key);
                        else next.delete(p.key);
                        setSelected(next);
                      }}
                      style={{
                        inlineSize: 18,
                        blockSize: 18,
                        marginBlockStart: 2,
                        accentColor: 'var(--zc-teal)',
                      }}
                    />
                    <span>
                      <span
                        style={{ fontSize: 13, fontWeight: 600, color: 'var(--zc-ink-strong)' }}
                      >
                        {permissionLabel(intl, p.key, props.catalog)}
                      </span>
                      {permissionDescription(intl, p.key, props.catalog) && (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--zc-muted)' }}>
                          {permissionDescription(intl, p.key, props.catalog)}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </FormCard>

      {editing && (
        <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
          <div className={a.row}>
            <span className={a.rowKey}>{t('roles.lifecycle')}</span>
            <span className={a.rowValue} style={{ fontSize: 12.5, color: 'var(--zc-ink-2)' }}>
              {inactive ? (
                <Chip kind="quiet">{t('roles.inactive')}</Chip>
              ) : (
                <Chip kind="ok">{t('roles.activeChip')}</Chip>
              )}{' '}
              {t(inactive ? 'roles.reactivateBody' : 'roles.deactivateHint')}
            </span>
            <span className={a.rowAction}>
              {inactive ? (
                <Button
                  variant="quiet"
                  busy={setActive.isPending}
                  disabled={setActive.isPending}
                  onClick={() => setActive.mutate(true)}
                >
                  {t('roles.reactivate')}
                </Button>
              ) : (
                <Button
                  variant="quiet"
                  disabled={setActive.isPending}
                  onClick={() => setConfirmingOff(true)}
                >
                  {t('roles.deactivate')}
                </Button>
              )}
            </span>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmingOff}
        title={intl.formatMessage(
          { id: 'roles.deactivateTitle' },
          { name: props.role?.name ?? '' },
        )}
        body={t('roles.deactivateBody') as string}
        effect={t('roles.deactivateEffect') as string}
        confirmLabel={t('roles.deactivateCta') as string}
        busy={setActive.isPending}
        onConfirm={() => setActive.mutate(false)}
        onClose={() => setConfirmingOff(false)}
      />
      <Notice kind="info">{t('roles.catalogNote')}</Notice>
    </div>
  );
}
